import { animate, AnimationEvent, style, transition, trigger } from '@angular/animations';
import { ConnectedPosition, OverlayRef } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    inject,
    LOCALE_ID,
    OnInit,
    Renderer2,
    signal,
    viewChild,
    viewChildren,
} from '@angular/core';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
    RouterModule,
} from '@angular/router';
import { SystemConfigService } from '@core/configs/system-config.service';
import { NotificationComponent } from '@core/layout/navigation/notification/notification.component';
import { Session } from '@core/models/auth.model';
import { ButtonDirective } from '@directives/button.directive';
import { DetectScrollDownDirective } from '@directives/detect-scroll-down.directive';
import { InkBarDirective } from '@directives/ink-bar.directive';
import { OverlayDirective } from '@directives/overlay.directive';
import { LanguageOptions, NavigationMenu } from '@models/common.model';
import { StoreModel } from '@models/store.model';
import { Store } from '@ngrx/store';
import { AuthDialogMode } from '@pages/auth/dialogs/auth-dialog/auth-dialog.component';
import { AuthService } from '@services/auth.service';
import { DialogService } from '@services/dialog.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter } from 'rxjs';

/**
 * Navigation component of booking fit
 *
 * @export
 * @class NavigationComponent
 * @implements {AfterViewInit}
 * @implements {OnInit}
 */
@Component({
    selector: 'pn-navigation',
    standalone: true,
    imports: [
        SvgIconComponent,
        ButtonDirective,
        RouterModule,
        CommonModule,
        InkBarDirective,
        OverlayDirective,
        DetectScrollDownDirective,
        NotificationComponent,
        NgOptimizedImage
    ],
    templateUrl: './navigation.component.html',
    styleUrl: './navigation.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('showHideMenu', [
            transition(':enter', [
                style({
                    transform: 'translateY(-120%)',
                    opacity: 0,
                    visibility: 'hidden',
                }),
                animate(
                    '500ms 450ms cubic-bezier(0.35, 0, 0.25, 1)',
                    style({
                        transform: 'none',
                        opacity: '*',
                        visibility: 'visible',
                    })
                ),
            ]),
            transition(':leave', [
                animate(
                    '220ms cubic-bezier(0.35, 0, 0.25, 1)',
                    style({ transform: 'translateY(-120%)' })
                ),
            ]),
        ]),
    ],
})
export class NavigationComponent implements AfterViewInit, OnInit {
    public inkBars = viewChildren(InkBarDirective);
    public menuButtonRef = viewChildren<OverlayDirective>(OverlayDirective);
    public nav = viewChild<ElementRef<HTMLElement>>('nav');
    public activeIndex = -1;
    public listMenu: NavigationMenu[] = [];

    public languageOptions: LanguageOptions[] = [
        {
            code: 'vi',
            title: $localize`:@@navigation.language.vietnamese:Tiếng Việt`,
            imageUrl: '/assets/images/flags/vn.png',
            baseHref: 'vi',
        },
        {
            code: 'en',
            title: $localize`:@@navigation.language.english:Tiếng Anh`,
            imageUrl: '/assets/images/flags/usa.png',
            baseHref: 'en',
        },
    ];
    public selectedLanguage: LanguageOptions = this.languageOptions[0];
    public selectedLanguageTemp: LanguageOptions = this.selectedLanguage;
    protected _isServer = !inject(Platform).isBrowser;
    protected _isMobile = this.device.isMobile();
    public session: Session | null = null;
    public showUserMenu = signal(false);
    public overlayUserDropdownPosition: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 30,
        },
    ];
    public overlayLanguageDropdownPosition: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 36,
        },
    ];
    public commonConfig = this._systemConfig.commonConfig;

    constructor(
        private readonly route: ActivatedRoute,
        public router: Router,
        private readonly dialogService: DialogService,
        private readonly device: DeviceDetectorService,
        private readonly cd: ChangeDetectorRef,
        private readonly _auth: AuthService,
        private readonly _render: Renderer2,
        @Inject(LOCALE_ID) private readonly _localeId: string,
        private readonly _store: Store<StoreModel>,
        private readonly _systemConfig: SystemConfigService
    ) {}

    ngOnInit(): void {
        this._getCurrentLang(this._localeId);
        this._getCurrentSession();
    }

    /**
     * Get list menu
     *
     * @memberof NavigationComponent
     */
    public getListMenu() {
        return [
            {
                title: $localize`:@@navigation.menu.home:Trang chủ`,
                link: '/',
                icon: '/assets/icons/home-2.svg',
                hidden: false,
            },
            {
                title: $localize`:@@navigation.menu.flight-ticket:Vé máy bay`,
                link: '/flight',
                icon: '/assets/icons/flight-2.svg',
                hidden: true,
            },
            {
                title: $localize`:@@navigation.menu.promotion:Khuyến mãi`,
                link: '/promotion',
                icon: '/assets/icons/ticket-discount.svg',
                hidden: false,
            },
            {
                title: this.session
                    ? $localize`:@@navigation.menu.order:Đơn hàng`
                    : $localize`:@@search-order.form.search:Đơn hàng`,
                link: this.session ? '/account/orders' : '/search-order',
                icon: '/assets/icons/document-justify-right-3.svg',
                hidden: false,
            },
            {
                title: $localize`:@@navigation.menu.new-agency:Tuyển đại lý`,
                link: '/new-agency',
                icon: '/assets/icons/people.svg',
                hidden: false,
            },
            {
                title: $localize`:@@navigation.menu.about:Giới thiệu`,
                link: '/about-us',
                icon: '/assets/icons/alert-1.svg',
                hidden: false,
            },
        ];
    }

    /**
     *  Get current locale
     *
     * @param lang
     * @private
     */
    private _getCurrentLang(lang: string) {
        const findLang = this.languageOptions.find((l) => lang === l.code);
        this.selectedLanguage = findLang || this.languageOptions[0];
    }

    ngAfterViewInit(): void {
        this._getActiveRoute();
    }

    /**
     * Get active menu
     *
     * @private
     */
    private _getActiveMenu(): void {
        const prevSelected = document.querySelector('.menu-item--active');
        const react = prevSelected?.getBoundingClientRect();
        const findIndex = this.listMenu.findIndex((item) => {
            const [pathname] = this._splitUrl(this.router.url.split('?')[0]);
            const [link] = this._splitUrl(item.link);
            return link === pathname;
        });
        this.activeIndex = findIndex === -1 ? 0 : findIndex;
        this._activeIndicatorBar(react);
        this.cd.detectChanges();
    }

    /**
     * Get active route
     *
     * @private
     */
    private _getActiveRoute(): void {
        if (this._isServer) {
            return;
        }
        this._getActiveMenu();
        this.router.events.pipe(filter((v) => v instanceof NavigationEnd)).subscribe(() => {
            this._getActiveMenu();
        });
    }

    /**
     * Get current session
     *
     * @private
     * @memberof NavigationComponent
     */
    private _getCurrentSession() {
        this._auth.session.subscribe((session) => {
            this.session = session;
            this.listMenu = this.getListMenu().filter((item) => !item.hidden);
            this.cd.detectChanges();
        });
    }

    /**
     * Active indicator bar
     *
     * @private
     * @param {DOMRect} [react]
     * @memberof NavigationComponent
     */
    private _activeIndicatorBar(react?: DOMRect): void {
        this.inkBars().forEach((inkBarDirective, i) => {
            if (i === this.activeIndex) {
                inkBarDirective.activeInkBar(react);
            } else {
                inkBarDirective.deactivateInkBar();
            }
        });
    }

    /**
     * Split url
     *
     * @private
     * @param {string} str
     * @return {*}  {string[]}
     * @memberof NavigationComponent
     */
    private _splitUrl(str: string): string[] {
        return str.split('/').filter((s) => s);
    }

    /**
     * Handle redirect to link
     *
     * @param {NavigationMenu} item
     * @param {number} index
     * @return {*}  {void}
     * @memberof NavigationComponent
     */
    public handleClick(item: NavigationMenu, index: number): void {
        if (item.link === this.router.url) {
            return;
        }

        // this.activeIndex = index;
        // const prevSelected = document.querySelector('.menu-item--active');
        // const react = prevSelected?.getBoundingClientRect();
        // this._activeIndicatorBar(react);
        this.router.navigateByUrl(item.link);
    }

    /**
     * Change language
     *
     * @param language
     */
    public changeLanguage(language: LanguageOptions): void {
        const { url } = this.router;
        window.location.href = '{origin}/{locale}{path}'.replaceWithParams({
            origin: location.origin,
            locale: language.baseHref,
            path: url,
        });
    }

    /**
     * Handle close sidebar
     *
     * @memberof NavigationComponent
     */
    public handleClosePopup(id: string): void {
        this.dialogService.closeDialogById(id);
    }

    /**
     *  Listen overlay open/close event
     *
     * @param event
     */
    public overlayOpen(event: boolean) {
        if (event) {
            this.selectedLanguageTemp = this.selectedLanguage;
            this.cd.detectChanges();
        }
    }

    /**
     * Open auth dialog
     *
     * @param {AuthDialogMode} mode
     * @memberof NavigationComponent
     */
    public openAuthDialog(mode: AuthDialogMode) {
        this._auth.openSignInDialog(mode).subscribe();
    }

    /**
     * Get auth check
     *
     * @private
     * @param {ActivatedRouteSnapshot} snapshot
     * @return {*}  {boolean}
     * @memberof NavigationComponent
     */
    private _getAuthCheck(snapshot: ActivatedRouteSnapshot): boolean {
        if (snapshot.data['authCheck']) {
            return true;
        }

        if (snapshot.firstChild) {
            return this._getAuthCheck(snapshot.firstChild);
        }

        return false;
    }

    /**
     * Handle logout
     *
     * @memberof NavigationComponent
     */
    public handleLogout() {
        this._auth.logout();
        const authCheck = this._getAuthCheck(this.route.snapshot);
        if (authCheck) {
            this.router.navigate(['/']);
        }
    }

    /**
     * overlay open/close event
     *
     * @param {boolean} event
     * @memberof NavigationComponent
     */
    public overlayOpenChange(event: boolean) {
        if (!event) {
            this.showUserMenu.set(false);
        }
    }

    /**
     * User menu animation start
     *
     * @param {AnimationEvent} event
     * @memberof NavigationComponent
     */
    public animationStarted(event: AnimationEvent) {
        this._render.addClass(event.element, 'absolute');
        this._render.addClass(event.element, 'top-0');
    }

    /**
     * User menu animation done
     *
     * @param {AnimationEvent} event
     * @memberof NavigationComponent
     */
    public animationCompleted(event: AnimationEvent) {
        this._render.removeClass(event.element, 'absolute');
    }

    /**
     * Handle navigate to path
     *
     * @param {string} path
     * @param {OverlayRef} [overlay]
     * @memberof NavigationComponent
     */
    public handleNavigateToPath(path: string, overlay?: OverlayRef) {
        this.router.navigate([path]).then((navigated) => {
            if (navigated) {
                overlay?.detach();
            }
        });
    }
}
